import { PostMessageData, Widget } from '@rocketlane/rli-internal-sdk';
import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
// import { ProjectAPI } from 'api/project';
import { confirm } from 'components/atoms/modal';
import { toastService } from 'components/molecules/toast';
import { CpModal } from 'components/organisms/customer-portal/components/modal/cp-modal';
import { LdsApiClient } from 'marketplace/api/local-dev-server-api';
import { ServerMethodApi } from 'marketplace/api/marketplace-service-api';
import { RlMarketplaceApi } from 'marketplace/api/rl-marketplace-api';
import { useAccountDataQuery } from 'queries/account';
import { logger } from 'services';
import { useGetMarketplaceAuthToken } from './use-marketplace-hooks';

export const useWidgetCommunicationHandler = (
  appId: number,
  {
    isCustomerPortal,
    isLds,
  }: {
    isCustomerPortal: boolean;
    isLds: boolean;
  } = {
    isCustomerPortal: false,
    isLds: false,
  },
  context?: Record<string, unknown>,
) => {
  const { data: accountData } = useAccountDataQuery();
  const show = useCallback(
    (event: PostMessageData) => {
      const { element, options } = event?.data ?? {};

      switch (element) {
        case 'dialog': {
          return new Promise((resolve) => {
            confirm({
              title: options?.title as string,
              content: options?.content as string | undefined,
              onOk: () => resolve(true),
              onCancel: () => resolve(false),
              getContainer: () =>
                document.getElementById('cp-modal-root') || document.body,
              CustomModal: isCustomerPortal ? CpModal : undefined,
            });
          });
        }

        case 'notification': {
          const { message, type = 'info' } = options ?? {};

          const toast = toastService({
            message,
          });

          const typeFn = toast[type];

          if (typeFn) {
            typeFn();
          }

          return Promise.resolve(true);
        }
        default:
          break;
      }

      return Promise.resolve(false);
    },
    [isCustomerPortal],
  );

  const registerCallbacks = useCallback(
    (
      widget: Widget,
      data?: Record<string, () => Promise<Record<string, unknown>>>,
    ) => {
      if (!widget) {
        return () => {};
      }

      const getData = async (event: PostMessageData) => {
        const eventName = event?.data?.dataIdentifier;

        if (data?.[eventName]) {
          return Promise.resolve({ response: await data?.[eventName]() });
        }

        if (eventName === 'user') {
          const user = accountData?.userProfile?.user;
          let responseUser = user;

          try {
            if (user) {
              responseUser = {
                ...user,
                permissionSet:
                  accountData?.userProfile?.permissions?.permissionSet,
              };
            }
          } catch (error) {
            logger.error('Error getting user permission set', { error });
          }

          return Promise.resolve({ response: responseUser });
        }

        if (eventName === 'account') {
          return Promise.resolve({ response: accountData?.account });
        }

        return Promise.resolve();
      };

      const invokeServerAction = (event: PostMessageData): Promise<unknown> => {
        if (!event?.data?.action) {
          return Promise.reject();
        }

        if (isLds) {
          return LdsApiClient.invokeServerMethod(
            event?.data?.action as string,
            {
              user: accountData?.userProfile?.user
                ? {
                    userId: accountData?.userProfile?.user?.userId,
                    emailId: accountData?.userProfile?.user?.emailId,
                  }
                : undefined,
              account: accountData?.account
                ? {
                    accountId: accountData?.account?.accountId,
                    accountName: accountData?.account?.accountName,
                  }
                : undefined,
              ...(context ?? {}),
            },
            event?.data?.data,
            event?.data?.options,
          );
        }

        return RlMarketplaceApi.runServerAction(
          appId,
          event?.data?.action as string,
          event?.data?.data,
          event?.data?.options,
        );
      };

      const getInvokeServerActionResponse = (event: PostMessageData) => {
        const requestId = event?.data?.requestId;

        if (!requestId) {
          return Promise.reject();
        }

        return RlMarketplaceApi.getInvokeServerActionResponse(appId, requestId);
      };

      widget.on('GET_DATA', getData);
      widget.on('INVOKE_SERVER_ACTION', invokeServerAction);
      widget.on('SHOW_INTERFACE', show);
      widget.on(
        'GET_INVOKE_SERVER_ACTION_RESPONSE',
        getInvokeServerActionResponse,
      );

      return () => {
        widget.off('GET_DATA', getData);
        widget.off('INVOKE_SERVER_ACTION', invokeServerAction);
        widget.off('SHOW_INTERFACE', show);
        widget.off(
          'GET_INVOKE_SERVER_ACTION_RESPONSE',
          getInvokeServerActionResponse,
        );
      };
    },
    [
      accountData?.account,
      accountData?.userProfile?.user,
      appId,
      isLds,
      show,
      context,
    ],
  );

  const invokeServerMethodLocal = useCallback(
    (name: string, payload: Record<string, unknown>) => {
      return LdsApiClient.invokeServerMethod(
        name,
        {
          user: accountData?.userProfile?.user,
          account: accountData?.account,
        },
        payload,
      );
    },
    [accountData?.account, accountData?.userProfile?.user],
  );

  const invokeServerMethod = useCallback(
    (name: string, payload: Record<string, unknown>) => {
      return RlMarketplaceApi.runServerAction(appId, name, payload);
    },
    [appId],
  );

  return {
    registerCallbacks,
    invokeServerMethodLocal,
    invokeServerMethod,
    show,
  };
};

export const useInvokeMarketplaceServerMethod = () => {
  useGetMarketplaceAuthToken();

  return useMutation(
    ({
      appId,
      name,
      payload,
    }: {
      appId: number;
      name: string;
      payload: Record<string, unknown>;
    }) => {
      return ServerMethodApi.invokeServerMethod(appId, name, payload);
    },
  );
};
